import { memo, useDeferredValue, useRef } from "react"
import type { FC, ReactElement, RefObject } from "react"

import { QueryClient, useQueryClient } from "@tanstack/react-query"
import isEmpty from "lodash-es/isEmpty"
import isNumber from "lodash-es/isNumber"
import map from "lodash-es/map"
import {
    type NavigateFunction,
    type Params,
    type SetURLSearchParams,
    useNavigate,
    useParams,
    useSearchParams
} from "react-router-dom"
import { useIntersection, useInterval, useMap, useUpdateEffect } from "react-use"
import type { StableActions } from "react-use/lib/useMap"

import { ArrowIcon } from "@/3514/assets"
import {
    Accordion,
    EAccordionBordersVariant,
    EAccordionColorVariant,
    EWaveLoaderVariant,
    type TAccordionRenderFunction,
    WaveLoader
} from "@/3514/components"
import { EPath } from "@/3514/constants"
import { type TUserState, useIsNetworkFast, useUser } from "@/3514/hooks"
import { coachingSessionsIndividualQueryPrefetcher, participantByIdQueryPrefetcher } from "@/3514/queries"
import { THEME } from "@/3514/theme"
import { type ISessionParticipantModel } from "@/3514/types"
import { ERoundDirection, getCssIdentifierFromFileName, propsAreEqual, seconds } from "@/3514/utils"

import { ECoachGroupQueryKey } from "./coach.group.api.config"
import queriesAdapters from "./coach.group.api.queries.adapters"
import { useCoachGroupSessionQueries as useQueries } from "./coach.group.api.queries.hooks"
import {
    type ICoachGroupSessionParticipantProgressModel as IParticipantModel,
    type ICoachGroupSessionSummaryDto as ISummaryDto
} from "./coach.group.api.types"
import SectionHeading from "./coach.group.component.section-heading"
import components from "./coach.group.helpers.individuals"
import styles from "./coach.group.styles"
import uiConfig from "./coach.group.ui.config"
import uiHooks from "./coach.group.ui.hooks"
import uiUtils from "./coach.group.ui.utils"

type TProps = { isSummaryAvailable: boolean; participantIds: number[] }
type TIndividualsConfig = { isSummaryLoaded: boolean; summaryId: number | undefined }
type TRowProps = {
    id: number
    groupId: number
    sessionId: number
    dataGetter(params: {
        qc: QueryClient
        individualId: number
        groupId: number
        sessionId: number
    }): ISessionParticipantModel
    canLoad: boolean
}

const {
    entities: { individuals: classes }
} = styles

const { INDIVIDUAL_LOADING_ARTIFICIAL_DELAY_IN_MS: DELAY_TIME } = uiConfig

const displayName: string = "CoachGroupIndividualsEntity"
const animationCssIdentifier: string = getCssIdentifierFromFileName(displayName, "r-a")

const { getIndividualPlaceholderData: getPlaceholderData } = uiUtils

function renderLoader(): ReactElement {
    return (
        <WaveLoader
            withRelativePosition
            variant={EWaveLoaderVariant.LightBlue}
            cssIdentifier={animationCssIdentifier}
        />
    )
}

const Row: FC<TRowProps> = memo(({ id, groupId, sessionId, dataGetter, canLoad }: TRowProps): ReactElement => {
    const navigate: NavigateFunction = useNavigate()

    const isNetworkFast: boolean = useIsNetworkFast()

    const qc: QueryClient = useQueryClient()

    const { user: coach }: TUserState = useUser()

    const ref: RefObject<HTMLElement> = useRef<HTMLElement>(null)

    const intersection: IntersectionObserverEntry = useIntersection(ref, {
        root: null,
        rootMargin: "10px",
        threshold: 0.1
    })

    const isQueryEnabled: boolean = uiHooks.useQueryDelayedEnabled(
        isNumber(id) && intersection?.isIntersecting && canLoad,
        DELAY_TIME
    )

    const incomingData: ISessionParticipantModel = dataGetter({
        qc,
        sessionId,
        groupId,
        individualId: id
    })

    const {
        individual: { data, isFetching, isFetched, isPlaceholderData, isError, isStale }
    }: ReturnType<typeof useQueries> = useQueries({
        individual: {
            enabled: isQueryEnabled,
            individualId: id,
            placeholderData: getPlaceholderData({
                firstName: incomingData.firstName,
                photo: incomingData.photo,
                coachPhoto: coach.photo
            })
        }
    })

    const deferredIsFetching: boolean = useDeferredValue<boolean>(isFetching)
    const deferredIsPlaceholderData: boolean = useDeferredValue<boolean>(isPlaceholderData)
    const deferredIsError: boolean = useDeferredValue<boolean>(isError)
    const deferredIsFetched: boolean = useDeferredValue<boolean>(isFetched)

    const deferredIsHeaderLoading: boolean = useDeferredValue<boolean>(
        deferredIsFetching || deferredIsPlaceholderData || isStale || !canLoad
    )

    const [rowProps, { set: updateRowProp }]: [IParticipantModel, StableActions<IParticipantModel>] =
        useMap<IParticipantModel>({
            ...(!!data
                ? { journeyLength: data.journeyLength, module: data.module, moduleSummary: data.moduleSummary }
                : {
                      ...queriesAdapters.individualProgress(
                          getPlaceholderData({
                              firstName: incomingData.firstName,
                              photo: incomingData.photo,
                              coachPhoto: coach.photo
                          })
                      )
                  }),
            id: incomingData.id,
            firstName: incomingData.firstName,
            lastName: incomingData.lastName,
            photo: incomingData.photo,
            isGuest: incomingData.isGuest
        })

    useUpdateEffect((): void => {
        if (!deferredIsFetched) return

        if (!deferredIsError) {
            updateRowProp("journeyLength", data.journeyLength)
            updateRowProp("module", data.module)
            updateRowProp("moduleSummary", data.moduleSummary)
        }
    }, [deferredIsFetched, deferredIsError, data])

    async function handleBodyButtonAction(userId: number): Promise<void> {
        return navigate(EPath.Coach_GroupParticipant({ groupId, participantId: userId }, { tab: "Modules" }))
    }

    async function handleBodyButtonHover(participantId: number): Promise<void> {
        if (isNetworkFast) {
            void participantByIdQueryPrefetcher({ qc }, { participantId })
            void coachingSessionsIndividualQueryPrefetcher({ qc }, { participantId })
        }
    }

    const renderHeader: TAccordionRenderFunction = (isRowExpanded: boolean): ReactElement => (
        <components.Header
            isRowExpanded={isRowExpanded}
            rowProps={rowProps}
            isDataLoading={deferredIsHeaderLoading}
            isVisible={intersection?.isIntersecting ? false : deferredIsFetched && !deferredIsError}
            isDataError={deferredIsError}
            isDataEmpty={isEmpty(data)}
            renderLoader={renderLoader}
        />
    )

    const renderBody: TAccordionRenderFunction = (_isRowExpanded: boolean): ReactElement => (
        <components.Body
            rowProps={rowProps}
            onButtonAction={handleBodyButtonAction}
            onButtonHover={handleBodyButtonHover}
        />
    )

    const renderIcon: TAccordionRenderFunction = (isRowExpanded: boolean): ReactElement => {
        return !deferredIsHeaderLoading && !deferredIsError ? (
            <ArrowIcon
                width={15}
                height={15}
                fillColor={THEME.COLORS.blue[200]}
                direction={isRowExpanded ? ERoundDirection.Top : ERoundDirection.Bottom}
                className={classes.row.header.icon()}
            />
        ) : null
    }

    return (
        <Accordion
            {...{ ref: deferredIsFetched ? undefined : ref }}
            renderHeader={renderHeader}
            renderBody={renderBody}
            renderIcons={[renderIcon]}
            isExpandable={!deferredIsHeaderLoading && !deferredIsError}
            bordersVariant={EAccordionBordersVariant.NoBordered}
            colorVariant={EAccordionColorVariant.White}
            withHoverColors
        />
    )
}, propsAreEqual)
Row.displayName = "CoachGroupIndividualsEntityRow"

/**
 * Coach "GroupSession page"' 'Individuals' block
 * Design spec {@link https://www.figma.com/design/QLhX4KcIViCdMD12xdfvXq/Coach?node-id=3370-44837&m=dev}
 *
 * @return ReactElement
 */
const CoachGroupIndividualsEntity: FC<TProps> = ({ isSummaryAvailable, participantIds }: TProps): ReactElement => {
    const { groupId: groupIdParam }: Readonly<Params> = useParams()

    const [searchParams]: [URLSearchParams, SetURLSearchParams] = useSearchParams()

    const sessionIdParam: string = searchParams.get("sessionId")

    const [config, { setAll: setConfig }]: [TIndividualsConfig, StableActions<TIndividualsConfig>] =
        useMap<TIndividualsConfig>({ isSummaryLoaded: false, summaryId: undefined })

    const queryClient: QueryClient = useQueryClient()

    const deferredIsSummaryLoaded: boolean = useDeferredValue(config.isSummaryLoaded)

    useInterval(
        (): void => {
            if (!isSummaryAvailable) {
                setConfig({ isSummaryLoaded: true, summaryId: undefined })
                return void 0
            }

            const summaryDto: ISummaryDto | undefined = queryClient.getQueryData(
                ECoachGroupQueryKey.SessionSummary({ sessionId: Number(sessionIdParam) })
            )

            if (isNumber(summaryDto?.id)) setConfig({ isSummaryLoaded: true, summaryId: summaryDto.id })

            return void 0
        },
        config.isSummaryLoaded ? null : seconds(1)
    )

    return isEmpty(participantIds) ? null : (
        <section className={classes.section.container}>
            <SectionHeading type="individuals" summaryId={config.summaryId} />

            <div className={classes.section.list}>
                {map<number, ReactElement>(
                    participantIds,
                    (p: number): ReactElement => (
                        <Row
                            key={`${displayName}-${p}`}
                            id={p}
                            groupId={Number(groupIdParam)}
                            sessionId={Number(sessionIdParam)}
                            dataGetter={uiUtils.getIndividualData}
                            canLoad={deferredIsSummaryLoaded}
                        />
                    )
                )}
            </div>
        </section>
    )
}

CoachGroupIndividualsEntity.displayName = displayName

export default CoachGroupIndividualsEntity
